import { motion, useInView } from 'framer-motion';
import { Star as PhosphorStar } from '@phosphor-icons/react';
import { BarChart3, Banknote, Bot, Brain, Briefcase, Building2, CheckCircle, FolderOpen, GraduationCap, Star, Target, Zap } from 'lucide-react';
import rajeshImg from '../assets/images/rajesh p.jpeg';
import naveenImg from '../assets/images/Naveen Raj.jpeg';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import placementPortfolio from '../assets/placement protfolio/Placement Portfolio.pdf';

/* eslint-disable @typescript-eslint/no-var-requires */
const homeClientLogos: string[] = [
  require('../assets/logo/Capture.PNG'),
  require('../assets/logo/images.png'),
  require('../assets/logo/images (1).png'),
  require('../assets/logo/images (2).png'),
  require('../assets/logo/images (3).png'),
  require('../assets/logo/unnamed.png'),
  require('../assets/logo/images.jfif'),
  require('../assets/logo/images (1).jfif'),
  require('../assets/logo/images (2).jfif'),
  require('../assets/logo/images (3).jfif'),
  require('../assets/logo/WhatsApp Image 2026-03-18 at 2.31.42 PM.jpeg'),
  require('../assets/logo/WhatsApp Image 2026-03-18 at 2.43.10 PM.jpeg'),
  require('../assets/logo/WhatsApp Image 2026-03-18 at 2.44.55 PM.jpeg'),
  require('../assets/logo/WhatsApp Image 2026-03-19 at 1.37.09 PM (3).jpeg'),
];

// ─── Animated Counter ────────────────────────────────────────────────────────
const Counter = ({ target, suffix = '' }: { target: number; suffix?: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 2000;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

// ─── Floating Orb Background ────────────────────────────────────────────────
const FloatingOrbs = () => (
  <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
    {[
      { size: 600, top: '-200px', left: '-200px', color: 'rgba(78,58,105,0.35)', delay: 0, duration: 20 },
      { size: 400, top: '30%', right: '-100px', color: 'rgba(240,180,41,0.15)', delay: 3, duration: 25 },
      { size: 300, bottom: '-100px', left: '30%', color: 'rgba(255,101,53,0.12)', delay: 6, duration: 18 },
    ].map((orb, i) => (
      <motion.div
        key={i}
        style={{
          position: 'absolute',
          width: orb.size,
          height: orb.size,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${orb.color} 0%, transparent 70%)`,
          top: orb.top,
          left: (orb as any).left,
          right: (orb as any).right,
          bottom: (orb as any).bottom,
        }}
        animate={{ scale: [1, 1.15, 1], rotate: [0, 60, 0] }}
        transition={{ duration: orb.duration, repeat: Infinity, delay: orb.delay, ease: 'easeInOut' }}
      />
    ))}
  </div>
);

// ─── Home ────────────────────────────────────────────────────────────────────
const Home = () => {
  const stats = [
    { value: 50, suffix: '%', label: '50% Pay After Placement', icon: <Banknote size={32} /> },
    { value: 60, suffix: ' Days', label: 'To Get Job-Ready', icon: <Zap size={32} /> },
  ];

  const pillars = [
    {
      weight: '40%',
      icon: <GraduationCap size={40} />,
      title: 'Education Institute',
      subtitle: '50% Pay After Placement',
      color: '#2b2240',
      gradient: 'linear-gradient(135deg, #2b2240, #4e3a69)',
      link: '/courses',
      cta: 'Explore Courses',
      points: [
        '2 Gen AI industry-ready programs',
        '60-day intensive training',
        'Placement till 1st offer',
        'Pay 50% after getting hired',
        '5+ real-world projects',
      ],
      badge: 'Most Popular',
    },
    {
      weight: '20%',
      icon: <Briefcase size={40} />,
      title: 'HR Consulting',
      subtitle: 'Talent That Delivers',
      color: '#F0B429',
      gradient: 'linear-gradient(135deg, #F0B429, #E8A317)',
      link: '/hr-consulting',
      cta: 'Hire Top Talent',
      points: [
        'Fresher hiring solutions',
        'Technical assessments',
        'Campus recruitment drives',
        'Resume & skill screening',
        'Mock interview panels',
        'HR process consulting',
      ],
      badge: 'Verified Talent',
    },
  ];

  const courses = [
    { title: 'GEN AI - AIML', icon: <Brain size={28} />, tag: 'Most Enrolled', color: '#8b5cf6', duration: '45 Days' },
    { title: 'GEN AI - Full Stack Analytics', icon: <BarChart3 size={28} />, tag: 'New Course', color: '#14b8a6', duration: '60 Days' },
  ];

  const testimonials = [
    { name: 'Rajesh P.', role: 'Backend Developer', course: 'Full Stack Python', image: rajeshImg, color: '#10b981', text: 'My trainer worked with me closely every day — reviewing code, guiding projects, prepping me for interviews. The 50% Pay After Placement model meant I could focus 100% on learning. Vista Tech\'s personal attention is what made all the difference.', rating: 5 },
    { name: 'Naveen Raj', role: 'ML Engineer', course: 'AI/ML + 50% PAP', image: naveenImg, color: '#F0B429', text: 'The AI track gave me a clear roadmap from Python basics to ML model deployment. My trainer\'s guidance during mock interviews boosted my confidence. Knowing I\'d only pay the remaining 50% after placement made me fully commit.', rating: 5 },
  ];

  const whyUs = [
    { icon: <Banknote size={26} />, title: '50% Pay After Placement', desc: 'Zero financial risk. Pay the second half only after you land your first job offer.', color: '#F0B429', highlight: true },
    { icon: <Bot size={26} />, title: 'AI-Powered Training', desc: 'Every course includes hands-on AI tools — automation agents, ChatGPT APIs, and more.', color: '#2b2240', highlight: false },
    { icon: <Building2 size={26} />, title: 'Top Hiring Partners', desc: 'Direct connections with top IT companies actively hiring our trained freshers.', color: '#3b82f6', highlight: false },
    { icon: <FolderOpen size={26} />, title: '5+ Real Projects', desc: 'Industry-standard portfolio projects that impress employers from day one.', color: '#8b5cf6', highlight: false },
    { icon: <Target size={26} />, title: 'Placement Till You\'re Hired', desc: 'We don\'t stop at one interview. Full placement support until your first offer.', color: '#FF6535', highlight: false },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const stagger = {
    visible: { transition: { staggerChildren: 0.12 } },
  };

  // ── Google Reviews (live via Netlify Function) ──────────────────────────────
  type GoogleReview = { author: string; rating: number; text: string; time: string; profilePhoto?: string };
  type GoogleData   = { name: string; rating: number; total: number; reviews: GoogleReview[] };

  const [googleData, setGoogleData]       = useState<GoogleData | null>(null);
  const [reviewsLoading, setReviewsLoading] = useState(true);

  useEffect(() => {
    fetch('/.netlify/functions/google-reviews')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.reviews) setGoogleData(data); })
      .catch(() => {})
      .finally(() => setReviewsLoading(false));
  }, []);

  // Static fallback — shown when API is unavailable
  const staticReviews = [
    { author: 'Anusha Govindan',      rating: 5, time: '24 hours ago', profilePhoto: undefined,
      text: 'Working at Vista Tech was a great experience. I was involved in hands-on tasks and practical work, which helped me gain real-time exposure. The guidance and support from the mentors made learning easy and effective. This opportunity helped me build strong skills and industry-level experience.' },
    { author: 'Vasanth Kumar',        rating: 5, time: '24 hours ago', profilePhoto: undefined,
      text: 'My experience working at Vista Tech was very good. I gained practical knowledge, worked on real-world projects, and learned professional work culture. The mentors provided continuous guidance, and the tasks assigned helped me improve my problem-solving skills. I am thankful for this opportunity.' },
    { author: 'Rajeshwari S',         rating: 5, time: '2 weeks ago',  profilePhoto: undefined,
      text: "Vista Tech transformed my skills! I joined without any coding background, and now I'm developing full-stack apps confidently. The trainers are patient, supportive, and available to clear doubts. Projects are real-world oriented — not just theory. Totally worth it!" },
    { author: 'Naveen Kumar',         rating: 5, time: '10 weeks ago', profilePhoto: undefined,
      text: "I'm working as a Technical Trainer at Vista Tech, and it's been an amazing experience. The organization provides a great environment for learning and skill development. We focus on practical, hands-on training to help students understand real-world applications. A great place to learn and grow in technology." },
    { author: 'Kalamesh Mani',        rating: 4, time: '10 weeks ago', profilePhoto: undefined,
      text: 'I attended the Limitless Interviews along with the Limitless Internship with stipend, and it was a great experience. The interview sessions helped me understand real interview patterns and improve my confidence. The internship gave me practical exposure, real-time tasks, and valuable industry experience. Overall, this program helped me grow technically and professionally.' },
    { author: 'Pooja Sivakumar',      rating: 5, time: '22 weeks ago', profilePhoto: undefined,
      text: 'I completed my Java Full Stack Development course here, and it was an amazing experience. The trainers were really supportive. Thanks to their guidance and placement support, I got placed in a good company after finishing the course. Totally recommend this institute to anyone who wants to start their IT career.' },
    { author: 'Viveka D',             rating: 5, time: '22 weeks ago', profilePhoto: undefined,
      text: 'I recently completed my Java Full Stack Development course, and it was an excellent learning experience. The course covered Java, Spring Boot, HTML, CSS, JavaScript, and MySQL. I had the chance to interact directly with the trainers, clear my doubts immediately, and participate in hands-on practice sessions. This course gave me strong technical knowledge and confidence to build complete web applications.' },
    { author: 'Dhayanidhi B',         rating: 4, time: '22 weeks ago', profilePhoto: undefined,
      text: 'I had a great learning experience in Vista Tech.' },
    { author: 'NISHANTH',             rating: 4, time: '22 weeks ago', profilePhoto: undefined,
      text: 'I had great mentoring from the Vista Tech developer, and they provided me with solid training.' },
  ];

  const avatarColors = ['#EA4335', '#4285F4', '#34A853', '#FBBC05', '#9C27B0', '#FF6535', '#00897B', '#F4511E', '#3949AB', '#8E24AA', '#039BE5'];
  const displayReviews: GoogleReview[] = (googleData?.reviews?.length ? googleData.reviews : staticReviews);
  const displayRating  = googleData?.rating  ?? 4.8;
  const displayTotal   = googleData?.total   ?? 11;

  return (
    <div className="min-h-screen font-poppins" style={{ overflowX: 'hidden' }}>

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <section
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0d0b14 0%, #2b2240 40%, #1a1030 100%)',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <FloatingOrbs />

        {/* Grid overlay */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />

        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '80px 1.5rem', position: 'relative', zIndex: 1, width: '100%' }}>
          <div className="grid-2col-hero" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'center' }}>

            {/* Left: Text */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  background: 'rgba(240,180,41,0.12)', border: '1px solid rgba(240,180,41,0.35)',
                  borderRadius: '50px', padding: '7px 18px', marginBottom: '28px',
                  color: '#F0B429', fontSize: '0.8rem', fontWeight: '700', letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              >
                <motion.span animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                  <Zap size={13} style={{ fill: '#F0B429' }} />
                </motion.span>
                India's First 50% Pay After Placement Institute
              </motion.div>

              <h1 style={{ fontSize: 'clamp(2.4rem, 5.5vw, 4.2rem)', fontWeight: '900', lineHeight: 1.1, marginBottom: '28px', letterSpacing: '-0.02em' }}>
                <span style={{ color: 'white', display: 'block' }}>India's #1 Institute with</span>
                <span style={{ background: 'linear-gradient(135deg, #F0B429 0%, #FF6535 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', display: 'block' }}>
                  50% Pay After Placement
                </span>
              </h1>

              <p style={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.75, marginBottom: '36px', maxWidth: '500px' }}>
                Vista Tech is <strong style={{ color: 'rgba(255,255,255,0.9)' }}>India's first institute</strong> to offer <strong style={{ color: 'rgba(255,255,255,0.9)' }}>50% Pay After Placement</strong>. Get job-ready in 60 days — pay the second half only after your first offer letter.
              </p>

              {/* CTA Buttons */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '40px' }}>
                <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}>
                  <Link to="/courses"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '8px',
                      padding: '15px 32px', borderRadius: '14px',
                      background: 'linear-gradient(135deg, #F0B429, #FF6535)',
                      color: 'white', fontWeight: '700', fontSize: '1rem',
                      textDecoration: 'none', boxShadow: '0 8px 32px rgba(240,180,41,0.45)',
                      letterSpacing: '0.01em',
                    }}
                  >
                    Explore Courses →
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}>
                  <a
                    href={placementPortfolio}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '8px',
                      padding: '15px 28px', borderRadius: '14px',
                      border: '1.5px solid rgba(255,255,255,0.2)',
                      color: 'white', fontWeight: '600', fontSize: '1rem',
                      textDecoration: 'none', backdropFilter: 'blur(12px)',
                      background: 'rgba(255,255,255,0.07)',
                    }}
                  >
                    <FolderOpen size={17} /> View Placements
                  </a>
                </motion.div>
              </div>

              {/* Trust row */}
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', paddingTop: '4px' }}>
                {[
                  { label: 'Placement Assured', icon: <CheckCircle size={15} /> },
                  { label: '60-Day Programs', icon: <Zap size={15} /> },
                  { label: 'No Job = No Remaining Fee', icon: <Banknote size={15} /> },
                ].map((t) => (
                  <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem' }}>
                    {t.icon} {t.label}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right: 50% PAP Feature Card + Stats */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, ease: 'easeOut', delay: 0.25 }}
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              {/* ── 50% Pay After Placement – hero card ── */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(240,180,41,0.18) 0%, rgba(255,101,53,0.12) 100%)',
                border: '1px solid rgba(240,180,41,0.35)',
                borderRadius: '24px',
                padding: '32px 28px',
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* background accent */}
                <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '160px', height: '160px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(240,180,41,0.25) 0%, transparent 70%)', pointerEvents: 'none' }} />
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#F0B429', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '10px' }}>
                    Our Signature Model
                  </div>
                  <div style={{ fontSize: 'clamp(2rem, 3.5vw, 2.8rem)', fontWeight: '900', color: 'white', lineHeight: 1.1, marginBottom: '12px', letterSpacing: '-0.02em' }}>
                    50% Pay After<br />
                    <span style={{ background: 'linear-gradient(135deg, #F0B429, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Placement</span>
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', lineHeight: 1.65, marginBottom: '24px' }}>
                    Start with just half the fee. Pay the rest <em>only</em> after you receive your first job offer. No job — no remaining payment.
                  </p>
                  {/* Payment split */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                    <div style={{ background: 'rgba(240,180,41,0.2)', border: '1px solid rgba(240,180,41,0.4)', borderRadius: '14px', padding: '14px', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.75rem', fontWeight: '900', color: '#F0B429', lineHeight: 1 }}>50%</div>
                      <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)', marginTop: '4px', fontWeight: '600' }}>Pay to Enroll</div>
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '1.25rem', fontWeight: '300' }}>+</div>
                    <div style={{ background: 'rgba(255,101,53,0.2)', border: '1px solid rgba(255,101,53,0.4)', borderRadius: '14px', padding: '14px', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.75rem', fontWeight: '900', color: '#FF6535', lineHeight: 1 }}>50%</div>
                      <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)', marginTop: '4px', fontWeight: '600' }}>After You're Hired</div>
                    </div>
                  </div>
                  <Link to="/enroll" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    padding: '12px', borderRadius: '12px',
                    background: 'linear-gradient(135deg, #F0B429, #FF6535)',
                    color: 'white', fontWeight: '700', fontSize: '0.9rem',
                    textDecoration: 'none', boxShadow: '0 6px 20px rgba(240,180,41,0.35)',
                  }}>
                    Enroll Now — Pay 50% Later →
                  </Link>
                </div>
              </div>

              {/* Stats row */}
              <div style={{
                background: 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '20px',
                padding: '20px 24px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0',
              }}>
                {stats.map((s, i) => (
                  <div key={s.label} style={{
                    padding: '14px 16px',
                    borderRight: i % 2 === 0 ? '1px solid rgba(255,255,255,0.07)' : 'none',
                    borderBottom: i < 2 ? '1px solid rgba(255,255,255,0.07)' : 'none',
                    display: 'flex', alignItems: 'center', gap: '12px',
                  }}>
                    <div style={{ color: 'rgba(240,180,41,0.7)', flexShrink: 0 }}>{s.icon}</div>
                    <div>
                      <div style={{ fontSize: '1.4rem', fontWeight: '800', color: '#F0B429', lineHeight: 1 }}>
                        <Counter target={s.value} suffix={s.suffix} />
                      </div>
                      <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.45)', marginTop: '3px', lineHeight: 1.3 }}>{s.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          style={{ position: 'absolute', bottom: '32px', left: '50%', transform: 'translateX(-50%)' }}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div style={{ width: '24px', height: '40px', border: '2px solid rgba(255,255,255,0.3)', borderRadius: '12px', display: 'flex', justifyContent: 'center', padding: '6px 0' }}>
            <div style={{ width: '3px', height: '8px', background: 'rgba(255,255,255,0.6)', borderRadius: '2px' }} />
          </div>
        </motion.div>
      </section>

      {/* ── STATS BAR ─────────────────────────────────────────────────────── */}
      <section style={{ background: 'linear-gradient(135deg, #2b2240, #4e3a69)', padding: '40px 1.5rem' }}>
        <div className="grid-4col-stats" style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '32px', textAlign: 'center' }}>
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px', color: 'rgba(240,180,41,0.85)' }}>{s.icon}</div>
              <div style={{ fontSize: '2rem', fontWeight: '800', color: '#F0B429', lineHeight: 1 }}>
                <Counter target={s.value} suffix={s.suffix} />
              </div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', marginTop: '6px' }}>{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── CLIENT LOGOS MARQUEE ──────────────────────────────────────────── */}
      <section style={{ padding: '70px 0', background: 'white', overflow: 'hidden' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px', padding: '0 1.5rem' }}>
          <p style={{ color: '#FF6535', fontSize: '0.78rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '10px' }}>Our Clients</p>
          <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: '900', color: '#2b2240', letterSpacing: '-0.02em' }}>
            Brands That <span style={{ color: '#FF6535' }}>Trust Vista Tech</span>
          </h2>
        </div>

        {/* Row 1 — left */}
        <div style={{ position: 'relative', overflow: 'hidden', marginBottom: '14px' }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '100px', background: 'linear-gradient(90deg,white,transparent)', zIndex: 2, pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '100px', background: 'linear-gradient(270deg,white,transparent)', zIndex: 2, pointerEvents: 'none' }} />
          <motion.div
            animate={{ x: ['0%', '-50%'] }}
            transition={{ duration: 32, repeat: Infinity, ease: 'linear' }}
            style={{ display: 'flex', gap: '18px', width: 'max-content' }}
          >
            {[...homeClientLogos, ...homeClientLogos].map((logo, i) => (
              <div key={i} style={{ flexShrink: 0, width: '150px', height: '84px', background: '#f8f7ff', borderRadius: '14px', border: '1.5px solid rgba(78,58,105,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px 18px' }}>
                <img src={logo} alt={`client-${i}`} style={{ maxWidth: '110px', maxHeight: '50px', objectFit: 'contain' }} />
              </div>
            ))}
          </motion.div>
        </div>

        {/* Row 2 — right */}
        <div style={{ position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '100px', background: 'linear-gradient(90deg,white,transparent)', zIndex: 2, pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '100px', background: 'linear-gradient(270deg,white,transparent)', zIndex: 2, pointerEvents: 'none' }} />
          <motion.div
            animate={{ x: ['-50%', '0%'] }}
            transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
            style={{ display: 'flex', gap: '18px', width: 'max-content' }}
          >
            {[...homeClientLogos.slice().reverse(), ...homeClientLogos.slice().reverse()].map((logo, i) => (
              <div key={i} style={{ flexShrink: 0, width: '150px', height: '84px', background: '#f8f7ff', borderRadius: '14px', border: '1.5px solid rgba(78,58,105,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px 18px' }}>
                <img src={logo} alt={`client-r-${i}`} style={{ maxWidth: '110px', maxHeight: '50px', objectFit: 'contain' }} />
              </div>
            ))}
          </motion.div>
        </div>

        <p style={{ textAlign: 'center', marginTop: '32px', color: '#9ca3af', fontSize: '0.85rem' }}>
          ⭐ Trusted by <strong style={{ color: '#2b2240' }}>50+ businesses</strong> across India
        </p>
      </section>

      {/* ── TWO PILLARS ───────────────────────────────────────────────────── */}
      <section style={{ padding: '100px 1.5rem', background: '#f8f7ff' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            style={{ textAlign: 'center', marginBottom: '64px' }}
          >
            <div style={{ display: 'inline-block', background: 'rgba(43,34,64,0.08)', borderRadius: '50px', padding: '6px 20px', fontSize: '0.875rem', fontWeight: '600', color: '#2b2240', marginBottom: '16px' }}>
              What We Do
            </div>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: '800', color: '#2b2240', marginBottom: '16px' }}>
              Two Pillars of{' '}
              <span style={{ background: 'linear-gradient(135deg, #2b2240, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Excellence
              </span>
            </h2>
            <p style={{ fontSize: '1.125rem', color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>
              Education and HR — two domains, one powerhouse company.
            </p>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={stagger}
            className="grid-2col" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '28px', maxWidth: '880px', margin: '0 auto' }}
          >
            {pillars.map((p, i) => (
              <motion.div
                key={p.title}
                variants={fadeUp}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                style={{
                  background: 'white',
                  borderRadius: '24px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 24px rgba(43,34,64,0.08)',
                  border: '1px solid rgba(43,34,64,0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Card header */}
                <div style={{ background: p.gradient, padding: '32px 28px 24px', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                  <div style={{ position: 'absolute', bottom: '-30px', left: '-10px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.2)', borderRadius: '50px', padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', color: 'white', marginBottom: '16px' }}>
                      {p.badge}
                    </div>
                    <div style={{ display: 'flex', marginBottom: '12px', color: 'rgba(255,255,255,0.9)' }}>{p.icon}</div>
                    <h3 style={{ fontSize: '1.375rem', fontWeight: '800', color: 'white', marginBottom: '4px' }}>{p.title}</h3>
                    <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem' }}>{p.subtitle}</p>
                  </div>
                </div>

                {/* Card body */}
                <div style={{ padding: '28px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {p.points.map((pt) => (
                      <li key={pt} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.875rem', color: '#4b5563' }}>
                        <span style={{ width: '20px', height: '20px', borderRadius: '50%', background: p.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>
                        </span>
                        {pt}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={p.link}
                    style={{
                      display: 'block', textAlign: 'center', padding: '12px 24px',
                      borderRadius: '10px', background: p.gradient,
                      color: 'white', fontWeight: '700', fontSize: '0.9rem',
                      textDecoration: 'none', marginTop: 'auto',
                      boxShadow: `0 4px 14px rgba(0,0,0,0.15)`,
                    }}
                  >
                    {p.cta} →
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── PAY AFTER PLACEMENT HIGHLIGHT ────────────────────────────────── */}
      <section style={{
        padding: '100px 1.5rem',
        background: 'linear-gradient(135deg, #0d0b14, #2b2240)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <FloatingOrbs />
        <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div className="grid-2col-hero" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(240,180,41,0.15)', border: '1px solid rgba(240,180,41,0.3)', borderRadius: '50px', padding: '6px 20px', fontSize: '0.875rem', fontWeight: '600', color: '#F0B429', marginBottom: '24px' }}>
                <Banknote size={15} /> Zero Financial Risk
              </div>
              <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: '800', color: 'white', lineHeight: 1.2, marginBottom: '24px' }}>
                India's First Institute with{' '}
                <span style={{ background: 'linear-gradient(135deg, #F0B429, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  50% Pay After Placement
                </span>
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.05rem', lineHeight: 1.7, marginBottom: '36px' }}>
                Vista Tech pioneered the <strong style={{ color: 'rgba(255,255,255,0.9)' }}>50% Pay After Placement</strong> model in India. Pay just 50% upfront; the rest only after your job offer. No offer? No remaining payment.
              </p>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <Link to="/enroll"
                  style={{
                    padding: '14px 28px', borderRadius: '12px',
                    background: 'linear-gradient(135deg, #F0B429, #FF6535)',
                    color: 'white', fontWeight: '700', fontSize: '1rem',
                    textDecoration: 'none', boxShadow: '0 8px 24px rgba(240,180,41,0.35)',
                  }}
                >
                  Enroll Now — Pay 50% Later
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              {[
                { step: '01', title: 'Enroll & Pay 50%', desc: 'Start your course with just half the fee. No bank loans needed.', color: '#F0B429' },
                { step: '02', title: '60-Day Training', desc: 'Intensive, project-based learning with expert trainers.', color: '#FF6535' },
                { step: '03', title: 'Get Placed', desc: 'Our placement team works until you receive your first offer.', color: '#4e3a69' },
                { step: '04', title: 'Pay the Remaining 50%', desc: 'Only after you\'re earning — fair, transparent, no surprises.', color: '#2b2240' },
              ].map((step) => (
                <div key={step.step}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: '20px',
                    background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '16px', padding: '20px 24px',
                  }}
                >
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '12px',
                    background: step.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.875rem', fontWeight: '800', color: 'white', flexShrink: 0,
                  }}>
                    {step.step}
                  </div>
                  <div>
                    <div style={{ fontWeight: '700', color: 'white', fontSize: '1rem', marginBottom: '4px' }}>{step.title}</div>
                    <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>{step.desc}</div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── COURSES GRID ──────────────────────────────────────────────────── */}
      <section style={{ padding: '100px 1.5rem', background: 'white' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            style={{ textAlign: 'center', marginBottom: '56px' }}
          >
            <div style={{ display: 'inline-block', background: '#f3f0ff', borderRadius: '50px', padding: '6px 20px', fontSize: '0.875rem', fontWeight: '600', color: '#2b2240', marginBottom: '16px' }}>
              Industry-Ready Programs
            </div>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: '800', color: '#2b2240', marginBottom: '12px' }}>
              Industry-Ready Courses
            </h2>
            <p style={{ color: '#6b7280', fontSize: '1.05rem' }}>
              All programs include AI-powered tools, real projects, and 50% Pay After Placement
            </p>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={stagger}
            className="grid-2col" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', maxWidth: '760px', margin: '0 auto 44px' }}
          >
            {courses.map((c) => (
              <motion.div
                key={c.title}
                variants={fadeUp}
                whileHover={{ y: -6, transition: { duration: 0.25 } }}
                style={{
                  background: 'white', borderRadius: '20px', overflow: 'hidden',
                  border: '1px solid rgba(43,34,64,0.07)', cursor: 'pointer',
                  boxShadow: '0 2px 16px rgba(43,34,64,0.06)',
                  transition: 'box-shadow 0.3s',
                  display: 'flex', flexDirection: 'column',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = `0 16px 48px ${c.color}25`; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 16px rgba(43,34,64,0.06)'; }}
              >
                {/* Colored top strip */}
                <div style={{ height: '4px', background: `linear-gradient(90deg, ${c.color}, ${c.color}aa)` }} />
                <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <div style={{ display: 'inline-flex', padding: '10px', borderRadius: '12px', background: `${c.color}12`, color: c.color }}>
                      {c.icon}
                    </div>
                    <span style={{
                      fontSize: '0.7rem', fontWeight: '700', padding: '4px 10px',
                      borderRadius: '50px', background: `${c.color}12`, color: c.color,
                      border: `1px solid ${c.color}25`,
                    }}>{c.tag}</span>
                  </div>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#2b2240', marginBottom: '12px', lineHeight: 1.3 }}>{c.title}</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: 'auto' }}>
                    {[c.duration, 'AI Tools', 'Placement Support'].map((tag) => (
                      <span key={tag} style={{ fontSize: '0.68rem', padding: '4px 10px', borderRadius: '6px', background: '#f8f7ff', color: '#6b7280', fontWeight: '500' }}>{tag}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div style={{ textAlign: 'center' }}>
            <Link to="/courses"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '14px 32px', borderRadius: '12px',
                background: 'linear-gradient(135deg, #2b2240, #4e3a69)',
                color: 'white', fontWeight: '700', fontSize: '1rem',
                textDecoration: 'none', boxShadow: '0 8px 24px rgba(43,34,64,0.3)',
              }}
            >
              View All Courses & Download Brochures →
            </Link>
          </div>
        </div>
      </section>

      {/* ── WHY VISTA TECH ────────────────────────────────────────────────── */}
      <section style={{ padding: '100px 1.5rem', background: '#f8f7ff' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            style={{ textAlign: 'center', marginBottom: '64px' }}
          >
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: '800', color: '#2b2240', marginBottom: '12px' }}>
              Why Choose{' '}
              <span style={{ background: 'linear-gradient(135deg, #2b2240, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Vista Tech
              </span>
            </h2>
            <p style={{ color: '#6b7280', fontSize: '1.05rem' }}>
              The only IT training institute in India offering 50% Pay After Placement — zero financial risk, maximum career support.
            </p>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={stagger}
            className="grid-3col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}
          >
            {whyUs.map((w) => (
              <motion.div
                key={w.title}
                variants={fadeUp}
                style={{
                  background: w.highlight ? `linear-gradient(135deg, rgba(240,180,41,0.07), rgba(255,101,53,0.05))` : 'white',
                  borderRadius: '20px', padding: '28px 28px 28px 24px',
                  border: w.highlight ? '1.5px solid rgba(240,180,41,0.35)' : '1px solid rgba(43,34,64,0.06)',
                  boxShadow: w.highlight ? '0 4px 24px rgba(240,180,41,0.12)' : '0 2px 16px rgba(43,34,64,0.05)',
                  borderLeft: `4px solid ${w.color}`,
                  display: 'flex', gap: '18px', alignItems: 'flex-start',
                }}
                whileHover={{ boxShadow: `0 12px 40px ${w.color}22`, y: -4 }}
                transition={{ duration: 0.3 }}
              >
                <div style={{ display: 'inline-flex', padding: '11px', borderRadius: '12px', background: `${w.color}12`, flexShrink: 0, color: w.color }}>{w.icon}</div>
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#2b2240', marginBottom: '8px' }}>{w.title}</h3>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem', lineHeight: 1.65 }}>{w.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────────────────────────── */}
      <section style={{ padding: '100px 1.5rem', background: 'white' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            style={{ textAlign: 'center', marginBottom: '56px' }}
          >
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: '800', color: '#2b2240', marginBottom: '12px' }}>
              Students Who Transformed Their Careers
            </h2>
            <p style={{ color: '#6b7280', fontSize: '1.05rem' }}>Real stories. Real placements. Real salaries.</p>
          </motion.div>

          <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -6 }}
                style={{
                  background: 'white', borderRadius: '24px', padding: '28px',
                  border: '1px solid rgba(43,34,64,0.07)',
                  boxShadow: '0 4px 20px rgba(43,34,64,0.06)',
                  position: 'relative', overflow: 'hidden',
                  transition: 'box-shadow 0.3s',
                  borderTop: `3px solid ${(t as any).color}`,
                }}
              >
                <div style={{ position: 'absolute', bottom: '12px', right: '20px', fontSize: '5rem', color: 'rgba(43,34,64,0.04)', lineHeight: 1, fontFamily: 'Georgia, serif', fontWeight: '900' }}>"</div>
                <div style={{ display: 'flex', gap: '3px', marginBottom: '14px' }}>
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} size={14} style={{ fill: '#F0B429', color: '#F0B429' }} />
                  ))}
                </div>
                <p style={{ color: '#4b5563', fontSize: '0.9rem', lineHeight: 1.75, marginBottom: '22px', position: 'relative', zIndex: 1 }}>"{t.text}"</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '18px', borderTop: '1px solid rgba(43,34,64,0.06)' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', flexShrink: 0, overflow: 'hidden', border: `2px solid ${(t as any).color}40` }}>
                    <img src={(t as any).image} alt={t.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '700', color: '#2b2240', fontSize: '0.95rem' }}>{t.name}</div>
                    <div style={{ color: '#9ca3af', fontSize: '0.76rem', marginTop: '1px' }}>{t.role}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: '600', color: (t as any).color, background: `${(t as any).color}12`, padding: '3px 10px', borderRadius: '50px', marginBottom: '3px' }}>{(t as any).course}</div>
                    <div style={{ fontSize: '0.68rem', fontWeight: '600', color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '3px 10px', borderRadius: '50px' }}>Placed ✓</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '40px', display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/success-stories"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '13px 28px', borderRadius: '12px',
                background: 'linear-gradient(135deg, #2b2240, #4e3a69)',
                color: 'white', fontWeight: '700', fontSize: '0.9rem', textDecoration: 'none',
                boxShadow: '0 6px 20px rgba(43,34,64,0.25)',
              }}
            >
              View All Success Stories →
            </Link>
            <a
              href={placementPortfolio}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '13px 28px', borderRadius: '12px',
                border: '2px solid #2b2240', color: '#2b2240',
                fontWeight: '700', fontSize: '0.9rem', textDecoration: 'none',
              }}
            >
              <FolderOpen size={16} /> View Placement Portfolio
            </a>
          </div>
        </div>
      </section>

      {/* ── GOOGLE REVIEWS ────────────────────────────────────────────────── */}
      <section style={{ padding: '80px 1.5rem', background: '#f8f7ff' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

          {/* Header */}
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            style={{ textAlign: 'center', marginBottom: '52px' }}
          >
            {/* Google G logo + rating */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
              {/* Google G SVG */}
              <svg width="36" height="36" viewBox="0 0 48 48" aria-label="Google">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.93 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              <div>
                <div style={{ fontSize: '2.6rem', fontWeight: '900', color: '#2b2240', lineHeight: 1 }}>{displayRating.toFixed(1)}</div>
                <div style={{ display: 'flex', gap: '3px', marginTop: '4px' }}>
                  {[0,1,2,3,4].map(i => (
                    <PhosphorStar key={i} size={18} weight="fill" color="#FBBC05" />
                  ))}
                </div>
              </div>
              <div style={{ borderLeft: '1px solid rgba(43,34,64,0.12)', paddingLeft: '16px' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#2b2240' }}>Google Reviews</div>
                <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '2px' }}>
                  {displayTotal ? `${displayTotal}+ verified reviews` : 'Verified by Google'}
                </div>
              </div>
            </div>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)', fontWeight: '800', color: '#2b2240', marginBottom: '10px' }}>
              What Our Students Say on{' '}
              <span style={{ color: '#4285F4' }}>Google</span>
            </h2>
            <p style={{ color: '#6b7280', fontSize: '1rem' }}>Real reviews from real students — verified on Google</p>
          </motion.div>

          {/* Review Cards */}
          {reviewsLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>Loading reviews…</div>
          ) : (
          <div className="grid-3col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '36px' }}>
            {displayReviews.map((r, i) => {
              const initials   = r.author.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
              const avatarColor = avatarColors[i % avatarColors.length];
              return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -4, boxShadow: '0 16px 40px rgba(43,34,64,0.1)' }}
                style={{
                  background: 'white', borderRadius: '20px', padding: '24px',
                  border: '1px solid rgba(43,34,64,0.07)',
                  boxShadow: '0 4px 18px rgba(43,34,64,0.06)',
                  transition: 'box-shadow 0.3s',
                  position: 'relative',
                }}
              >
                {/* Google G watermark */}
                <div style={{ position: 'absolute', top: '16px', right: '20px', opacity: 0.12 }}>
                  <svg width="28" height="28" viewBox="0 0 48 48">
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.93 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                </div>

                {/* Reviewer info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                  {r.profilePhoto ? (
                    <img src={r.profilePhoto} alt={r.author} style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '800', fontSize: '1rem', flexShrink: 0 }}>
                      {initials}
                    </div>
                  )}
                  <div>
                    <div style={{ fontWeight: '700', color: '#2b2240', fontSize: '0.95rem' }}>{r.author}</div>
                    <div style={{ display: 'flex', gap: '3px', marginTop: '3px' }}>
                      {[...Array(r.rating)].map((_, j) => <PhosphorStar key={j} size={13} weight="fill" color="#FBBC05" />)}
                    </div>
                  </div>
                  <div style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#9ca3af' }}>{r.time}</div>
                </div>

                {/* Review text */}
                <p style={{ color: '#4b5563', fontSize: '0.875rem', lineHeight: 1.72 }}>"{r.text}"</p>
              </motion.div>
              );
            })}
          </div>
          )}

          {/* See all on Google button */}
          <div style={{ textAlign: 'center' }}>
            <a
              href="https://maps.app.goo.gl/sjz7bY3cbBdHQ1t3A"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                padding: '13px 28px', borderRadius: '12px',
                background: 'white', border: '1.5px solid rgba(43,34,64,0.15)',
                color: '#2b2240', fontWeight: '700', fontSize: '0.9rem',
                textDecoration: 'none', boxShadow: '0 4px 14px rgba(43,34,64,0.08)',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.93 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              See All Reviews on Google
            </a>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ─────────────────────────────────────────────────────── */}
      <section style={{
        padding: '120px 1.5rem',
        background: 'linear-gradient(135deg, #0d0b14 0%, #2b2240 50%, #1a1030 100%)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <FloatingOrbs />
        {/* Grid overlay */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        <div style={{ maxWidth: '720px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(240,180,41,0.12)', border: '1px solid rgba(240,180,41,0.3)', borderRadius: '50px', padding: '7px 20px', fontSize: '0.8rem', fontWeight: '700', color: '#F0B429', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '24px' }}>
              <motion.span animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                <Zap size={13} style={{ fill: '#F0B429' }} />
              </motion.span>
              Your Career Starts Today
            </div>
            <h2 style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', fontWeight: '900', color: 'white', marginBottom: '20px', lineHeight: 1.15, letterSpacing: '-0.02em' }}>
              Ready to{' '}
              <span style={{ background: 'linear-gradient(135deg, #F0B429, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Change Your Life?
              </span>
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '48px', maxWidth: '560px', margin: '0 auto 48px' }}>
              Whether you want to launch a tech career, scale your business, or hire top talent — Vista Tech is your trusted partner.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}>
                <Link to="/enroll"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '16px 36px', borderRadius: '14px',
                    background: 'linear-gradient(135deg, #F0B429, #FF6535)',
                    color: 'white', fontWeight: '700', fontSize: '1.05rem',
                    textDecoration: 'none', boxShadow: '0 8px 32px rgba(240,180,41,0.45)',
                  }}
                >
                  Start Your Journey →
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}>
                <a
                  href="https://wa.me/919894449002"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '16px 36px', borderRadius: '14px',
                    border: '1.5px solid rgba(255,255,255,0.2)',
                    color: 'white', fontWeight: '600', fontSize: '1.05rem',
                    textDecoration: 'none', backdropFilter: 'blur(12px)',
                    background: 'rgba(255,255,255,0.07)',
                  }}
                >
                  Talk to an Expert
                </a>
              </motion.div>
            </div>
            {/* Trust row */}
            <div style={{ display: 'flex', gap: '28px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '44px' }}>
              {[
                { label: 'No Job = No Remaining Fee', icon: <Banknote size={14} /> },
              ].map((t) => (
                <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>
                  {t.icon} {t.label}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
